import { Badge } from '@atrvd/saas-ui'
import {
  CAPABILITY_ORDER,
  CAPABILITY_LABEL,
  UNSUPPORTED_CAPABILITY_STATUS,
  readCapabilityStatus,
  type CapabilityKey,
} from './ledgerSyncConnectedBanks.utils'

/** Props for the capability badge row. */
export interface CapabilityBadgesProps {
  capabilities: Record<string, unknown> | null
}

/**
 * Render compact capability chips from a connection's realized_capabilities.
 *
 * EVERY CHIP IS GREY, deliberately. The chips name which capabilities the bank
 * exposes; they are not a health indicator, and the prototype renders one flat
 * pill style for all of them (`capPill()` in "Banking Settings (3).html" has no
 * status branch). An earlier version coloured them by `status` — green for
 * `succeeded`, yellow for `pending` — which read as an alert the row never
 * meant to raise.
 *
 * A capability reported `unsupported` is SKIPPED rather than greyed out. That
 * distinction used to be carried by the colour; now that every chip looks the
 * same, rendering one would claim the bank offers something it does not. AC#3
 * asks for "the enabled capabilities", so absent is the honest rendering.
 *
 * Renders a muted dash when nothing qualifies, so the column stays aligned.
 */
export function CapabilityBadges({ capabilities }: CapabilityBadgesProps) {
  const chips = CAPABILITY_ORDER.map((key) => ({
    key,
    status: readCapabilityStatus(capabilities, key),
  })).filter(
    (c): c is { key: CapabilityKey; status: string } =>
      c.status !== null && c.status !== UNSUPPORTED_CAPABILITY_STATUS
  )

  if (chips.length === 0) {
    return <span className="text-muted-foreground">—</span>
  }

  return (
    <div className="flex flex-wrap gap-1">
      {chips.map(({ key }) => (
        <Badge key={key} variant="muted">
          {CAPABILITY_LABEL[key]}
        </Badge>
      ))}
    </div>
  )
}
